import React, { useMemo } from 'react';
import styled from "styled-components";
import Calendar, { CalendarProps } from '@shared/ui/molecules/calendar/Calendar';
import moment, { Moment } from "moment";
import {
  companyHolidayToHighlightedDayConverter,
  customLeaveDayToHighlightedDayConverter,
  HolidayDay,
  holidayToHighlightedDayConverter,
  nonWorkingDayToHighlightedDayConverter
} from "../../models/Holidays.model";
import { CompanyHoliday } from '@features/holidays/services/CompanyHolidayService';
import { DATE_FORMAT } from '@shared/utils/DateUtils';
import { CompanyHolidayInfoPopup } from "@features/wall-planner/pages/components/CompanyHolidayInfoPopup";
import { PopupPosition } from "@features/wall-planner/pages/components/HolidayInfoAndApprovalPopup";
import { SimpleHolidayInfoPopup } from "@features/wall-planner/pages/components/SimpleHolidayInfoPopup";
import { CustomLeaveDays, CustomNonWorkingDays, NonWorkingDays } from '@features/holidays/services/HolidayService';
import { NonWorkingDayInfoPopup } from "@features/wall-planner/pages/components/NonWorkingDayInfoPopup";
import { CustomLeaveInfoPopup } from "@features/wall-planner/pages/components/CustomLeaveInfoPopup";
import { SideOfDay } from '@features/whereabouts/models/movements.models';

function filterHolidaysOnMonth(h: HolidayDay | CompanyHoliday | NonWorkingDays | CustomLeaveDays, monthDate: Moment) {
  return !moment(h.date).isBefore(monthDate.startOf('month')) && !moment(h.date).isAfter(monthDate.endOf('month'))
}

export function HolidayCalendarMonthView(props: Props) {
  const {monthDate, holidaysBooked, colourOverride, companyHolidays, nonWorkingDays, customNonWorkingDays, customLeaveDays} = props;
  const daysBookedInMonth = useMemo(() => {
    const usersHolidays = holidaysBooked
      .filter(h => filterHolidaysOnMonth(h, monthDate))
      .map((bookedHoliday) => holidayToHighlightedDayConverter(bookedHoliday))
      .map(highlightedDay => !!colourOverride ? ({...highlightedDay, color: colourOverride}) : highlightedDay)

    const filteredCompanyHolidays = companyHolidays.filter(ch => filterHolidaysOnMonth(ch, monthDate))
      .map((bookedHoliday) => companyHolidayToHighlightedDayConverter(bookedHoliday))

    const filteredNonWorkingDays = nonWorkingDays.filter(ch => filterHolidaysOnMonth(ch, monthDate))
      .map((day) => nonWorkingDayToHighlightedDayConverter(day))

    const filteredCustomLeaveDays = customLeaveDays.filter(ch => filterHolidaysOnMonth(ch, monthDate))
      .map((day) => customLeaveDayToHighlightedDayConverter(day))

    return [...usersHolidays, ...filteredCompanyHolidays, ...filteredNonWorkingDays, ...filteredCustomLeaveDays];
  }, [holidaysBooked, companyHolidays, nonWorkingDays, customLeaveDays, monthDate, colourOverride]);

  const onDateSelect = (date: any) => {
    if (companyHolidays.length > 0) {
      const ch = companyHolidays.find(c => c.date === date.format(DATE_FORMAT));
      if (ch && props.onCompanyHolidayClick) {
        props.onCompanyHolidayClick(ch);
      }
    }
  }

  return (
    <CalendarWrapper onDateSelect={onDateSelect}
                     initialDate={monthDate}
                     highlightedDays={daysBookedInMonth}
                     hideActiveDayHighlight={true}
                     hideFlicker={true}
                     hideDaysOutsideOfMonth={true}
                     enabledStartDate={props.enabledStartDate}
                     enabledEndDate={props.enabledEndDate}
                     renderOnHover={(date: Moment) => <OnHover date={date}
                                                               companyHolidays={companyHolidays}
                                                               nonWorkingDays={nonWorkingDays}
                                                               customNonWorkingDays={customNonWorkingDays ?? []}
                                                               customLeaveDays={customLeaveDays}
                                                               holidays={holidaysBooked} />}/>
  )
}

interface Props {
  monthDate: Moment;
  companyHolidays: CompanyHoliday[];
  holidaysBooked: HolidayDay[];
  colourOverride?: string;
  enabledStartDate?: Moment;
  enabledEndDate?: Moment;
  nonWorkingDays: NonWorkingDays[];
  customNonWorkingDays?: CustomNonWorkingDays[];
  customLeaveDays: CustomLeaveDays[];
  onCompanyHolidayClick?: (ch: CompanyHoliday) => any;
}

function OnHover(props: {holidays: HolidayDay[], companyHolidays: CompanyHoliday[], nonWorkingDays: NonWorkingDays[], customNonWorkingDays: CustomNonWorkingDays[], customLeaveDays: CustomLeaveDays[], date: Moment}) {
  const {companyHolidays, holidays, nonWorkingDays, customNonWorkingDays, date, customLeaveDays} = props;
  const holiday = useMemo(() => holidays.find(h => h.date === date.format(DATE_FORMAT)), [holidays, date])
  const companyHoliday = useMemo(() => companyHolidays.find(h => h.date === date.format(DATE_FORMAT)), [companyHolidays, date])
  const nonWorkingDay = useMemo(() => nonWorkingDays.find(h => h.date === date.format(DATE_FORMAT)), [nonWorkingDays, date])
  const customNonWorkingDay = useMemo(() => customNonWorkingDays.find(h => h.date === date.format(DATE_FORMAT)), [customNonWorkingDays, date])
  const customLeaveDay = useMemo(() => customLeaveDays.find(h => h.date === date.format(DATE_FORMAT)), [customLeaveDays, date])

  if (!!holiday && !!companyHoliday) {
    return null;
  } else if (companyHoliday) {
    return <PopupWrapper>
      <CompanyHolidayInfoPopup companyHoliday={companyHoliday}
                               popupPositioning={PopupPosition.None} />
    </PopupWrapper>
  } else if (holiday) {
    return <PopupWrapper>
      <SimpleHolidayInfoPopup holiday={holiday}
                              popupPositioning={PopupPosition.None} />
    </PopupWrapper>
  } else if (nonWorkingDay) {
    return <PopupWrapper>
      <NonWorkingDayInfoPopup nonWorkingDay={nonWorkingDay}
                              popupPositioning={PopupPosition.None} />
    </PopupWrapper>
  } else if (customNonWorkingDay) {
    return <PopupWrapper>
      <NonWorkingDayInfoPopup nonWorkingDay={customNonWorkingDay}
                              popupPositioning={PopupPosition.None} />
    </PopupWrapper>
  } else if (customLeaveDay) {
    return <PopupWrapper>
      <CustomLeaveInfoPopup customLeaveDay={customLeaveDay}
                            sideOfDay={SideOfDay.AllDay}
                            showBothSidesOfDayIfDifferent={true}
                            popupPositioning={PopupPosition.None} />
    </PopupWrapper>
  } else {
    return null;
  }
}


const CalendarWrapper = styled(Calendar)<CalendarProps>`
  margin: 16px;
`

const PopupWrapper = styled.div`
  position: absolute;
  .popup {
    display: flex !important;
  }
`
